// The one image attached to the current Browse search.
//
// Two Preact roots render it — the "Search with image" button beside the search box and the
// "Search by image" section at the top of the filter sidebar — and `navigation.ts` reads it
// when it builds the `lm_filters` payload. None of the three can own the state, so it lives
// here.
//
// A typed subscriber list rather than a hidden input (the route `frontAdvanced` takes for
// its three values): the payload is an object with a 6 kB vector in it, and stuffing that
// through `#lm_adv_*`-style string inputs would mean serialising on every write and parsing
// on every read, with nothing checking the shape at either end.

export interface SearchImage {
  /** File name, shown next to the thumbnail. */
  name: string;
  /** Downscaled `data:` URL — the thumbnail. The file itself is never uploaded or stored. */
  preview: string;
  /**
   * The SigLIP query vector, base64 of the raw `Float32Array` bytes.
   *
   * Embedded ONCE when the file is picked, then carried in every `search.load` body so
   * paging re-ranks without re-embedding. Decoded server-side by `vecFromB64`.
   */
  vecB64: string;
}

type Listener = (image: SearchImage | null) => void;

let current: SearchImage | null = null;
const listeners = new Set<Listener>();

/** The attached image, or null. Read by `navigation.ts` when building the filter payload. */
export function getSearchImage(): SearchImage | null {
  return current;
}

/** Attach an image, or clear it with `null`. Notifies both roots. */
export function setSearchImage(image: SearchImage | null): void {
  current = image;
  for (const fn of [...listeners]) fn(current);
}

/**
 * Subscribe, and receive the current value immediately.
 *
 * The immediate call matters: the two roots mount independently, so the sidebar section
 * cannot assume it was listening when the button set the image.
 */
export function onSearchImageChange(fn: Listener): () => void {
  listeners.add(fn);
  fn(current);
  return () => {
    listeners.delete(fn);
  };
}
